/**
 * Scelta dell'entita' da mostrare come interlocutore di un thread email.
 *
 * Fino al 30 luglio 2026 la schermata prendeva la prima associazione trovata fra i
 * messaggi: in un thread misto era quasi sempre quella del messaggio inviato
 * dall'utente, cioe' la scheda dell'utente stesso. Qui la scelta e' esplicita e
 * scarta le schede interne, cosi' la vista e' corretta anche sui dati gia' sbagliati.
 *
 * Modulo puro: i test del progetto girano in ambiente node, senza Supabase.
 */
import type { MailMessage } from '../stores/mail/types';

export type LinkedClient = NonNullable<MailMessage['client']>;

function normalize(email?: string | null): string {
  return (email || '').trim().toLowerCase();
}

/** Una scheda e' "interna" quando la sua email appartiene a un utente dell'organizzazione. */
export function isInternalClient(client: LinkedClient | null | undefined, internalEmails: Set<string>): boolean {
  if (!client) return false;
  const email = normalize(client.email);
  return !!email && internalEmails.has(email);
}

function isSent(message: MailMessage): boolean {
  return (message.label_ids || []).includes('SENT');
}

function byDateDesc(a: MailMessage, b: MailMessage): number {
  return new Date(b.internal_date).getTime() - new Date(a.internal_date).getTime();
}

/**
 * Entita' a cui il thread e' collegato.
 *
 * 1. scarta i messaggi la cui scheda e' interna all'organizzazione;
 * 2. preferisce l'associazione del messaggio ricevuto piu' recente, perche' e' li'
 *    che sta l'interlocutore vero;
 * 3. in mancanza di messaggi ricevuti, la piu' recente qualunque essa sia.
 */
export function pickThreadClient(
  messages: MailMessage[],
  internalEmails: Set<string> = new Set(),
): LinkedClient | null {
  const withClient = messages.filter(m => !!m.client && !isInternalClient(m.client, internalEmails));
  if (withClient.length === 0) return null;

  const inbound = withClient.filter(m => !isSent(m));
  const pool = inbound.length > 0 ? inbound : withClient;
  const sorted = [...pool].sort(byDateDesc);
  return sorted[0]?.client ?? null;
}

/**
 * Indirizzo della controparte esterna del thread: il mittente del messaggio ricevuto
 * piu' recente. Serve a cercare la persona da mostrare accanto all'azienda.
 */
export function findThreadSenderEmail(
  messages: MailMessage[],
  internalEmails: Set<string> = new Set(),
): string | null {
  const inbound = messages
    .filter(m => !isSent(m))
    .filter(m => {
      const from = normalize(m.from_email);
      return !!from && !internalEmails.has(from);
    })
    .sort(byDateDesc);

  if (inbound.length > 0) return normalize(inbound[0].from_email);

  // Thread di sola posta inviata: la controparte e' il primo destinatario esterno.
  for (const m of [...messages].sort(byDateDesc)) {
    for (const t of m.to_emails || []) {
      const email = normalize(t.email);
      if (email && !internalEmails.has(email)) return email;
    }
  }
  return null;
}

// ─────────────────────────────────────────────────────────────
// Riquadro "crea la persona" su un'azienda gia' in anagrafica
// ─────────────────────────────────────────────────────────────

export type PersonPromptDecision = 'create' | 'link' | 'none';

/**
 * Cosa proporre dopo aver collegato il thread a un'azienda esistente.
 * `none` anche quando manca l'indirizzo del mittente: senza email non c'e' persona
 * da proporre.
 */
export function decidePersonPrompt(args: {
  senderEmail: string | null;
  existingPersonId: string | null;
  alreadyLinked: boolean;
}): PersonPromptDecision {
  if (!args.senderEmail) return 'none';
  if (!args.existingPersonId) return 'create';
  return args.alreadyLinked ? 'none' : 'link';
}
